import React, { useEffect, useState } from "react";
import { Tag } from "@familyzone/component-library";
import { UserStoreUser, useUserStore } from "../storez/UserStore";
import { fullNameAndUsername, getFullName } from "../components/UserSearch/UserSearchHelper";

/**
 * StudentNamePill component displays a pill with the student's name.
 *
 * When passed a user object, its properties are used to display the full name;
 * otherwise, user details will be fetched based on the username provided.
 *
 * @param {(string|UserStoreUser)} user - student username or user object.
 * @returns {JSX.Element} A Tag component displaying the student's name.
 */
type UsernameOrUser =
  | {
      username: string;
      user?: never;
    }
  | {
      username?: never;
      user: UserStoreUser;
    };

const StudentNamePill: React.FC<UsernameOrUser> = ({ username, user }) => {
  const [displayName, setDisplayName] = useState(username ?? user?.username ?? "");

  useEffect(() => {
    if (user) {
      setDisplayName(fullNameAndUsername(user.username, getFullName(user)));
    } else if (username) {
      void useUserStore
        .getState()
        .getUser(username)
        .then((user) => {
          if (!user) setDisplayName(username);
          else setDisplayName(fullNameAndUsername(user.username, getFullName(user)));
        })
        .catch(() => {
          setDisplayName(username);
        });
    }
  }, [username, user]);

  return <Tag isRemovable={false} tagLabel={displayName} tagLeftIcon="fa fa-user-circle" variant="dark" data-testid="studentNamePill" />;
};

export default StudentNamePill;
